import { MAX_SEGMENT_MS, MEETING_SAMPLE_RATE, MIN_SEGMENT_MS } from '@hub/contracts'

/**
 * The voice-activity segmenter (PLAN-085 §1.6). Pure: it takes 16 kHz PCM16
 * frames and says where a spoken segment starts and ends. The browser glue
 * (`transcriber.ts`) feeds it and ships what it emits.
 *
 * Why cut on silence rather than on a fixed clock: Whisper transcribes a
 * whole utterance far better than a sentence sliced in half, and a segment
 * that ends where the speaker paused is also where a reader expects a line to
 * end in the transcript.
 */

/** A pause this long ends the segment. */
export const SILENCE_TAIL_MS = 700
/** Below this much actual speech, a segment is noise and is dropped. */
export const MIN_SPEECH_MS = MIN_SEGMENT_MS
/** RMS above this counts as speech. PCM16 full scale is 32768. */
export const SPEECH_RMS_THRESHOLD = 500
/** The worklet posts 20 ms frames. */
export const FRAME_MS = 20

export interface Segment {
  /** Milliseconds since the segmenter started, i.e. since the meeting joined. */
  startMs: number
  endMs: number
  pcm: Int16Array
}

export function frameRms(frame: Int16Array): number {
  if (frame.length === 0) return 0
  let sum = 0
  for (const sample of frame) sum += sample * sample
  return Math.sqrt(sum / frame.length)
}

export interface SegmenterOptions {
  silenceTailMs?: number
  minSpeechMs?: number
  maxSegmentMs?: number
  threshold?: number
  sampleRate?: number
}

/**
 * Feed it frames, take segments out. Muting is the caller's job: while the
 * microphone is off nothing is fed in, so nothing is produced (PLAN-085
 * §1.6 — "Muted ⇒ nothing is sent").
 */
export class VadSegmenter {
  #buffer: Int16Array[] = []
  #bufferSamples = 0
  #elapsedMs = 0
  #segmentStartMs: number | null = null
  #silenceMs = 0
  #speechMs = 0

  readonly #silenceTailMs: number
  readonly #minSpeechMs: number
  readonly #maxSegmentMs: number
  readonly #threshold: number
  readonly #sampleRate: number

  constructor(options: SegmenterOptions = {}) {
    this.#silenceTailMs = options.silenceTailMs ?? SILENCE_TAIL_MS
    this.#minSpeechMs = options.minSpeechMs ?? MIN_SPEECH_MS
    this.#maxSegmentMs = options.maxSegmentMs ?? MAX_SEGMENT_MS
    this.#threshold = options.threshold ?? SPEECH_RMS_THRESHOLD
    this.#sampleRate = options.sampleRate ?? MEETING_SAMPLE_RATE
  }

  /** Milliseconds of audio seen so far — the segment timeline. */
  get elapsedMs(): number {
    return this.#elapsedMs
  }

  /** Feed one frame; returns a segment when this frame completed one. */
  push(frame: Int16Array): Segment | null {
    const frameMs = Math.round((frame.length * 1000) / this.#sampleRate)
    const speech = frameRms(frame) > this.#threshold
    this.#elapsedMs += frameMs

    if (speech) {
      if (this.#segmentStartMs === null) {
        this.#segmentStartMs = this.#elapsedMs - frameMs
        this.#silenceMs = 0
        this.#speechMs = 0
      }
      this.#silenceMs = 0
      this.#speechMs += frameMs
    } else if (this.#segmentStartMs === null) {
      // Silence outside a segment is not worth buffering.
      return null
    } else {
      this.#silenceMs += frameMs
    }

    this.#buffer.push(frame)
    this.#bufferSamples += frame.length

    const lengthMs = this.#elapsedMs - this.#segmentStartMs!
    if (this.#silenceMs >= this.#silenceTailMs || lengthMs >= this.#maxSegmentMs) {
      return this.#cut()
    }
    return null
  }

  /** End the current segment now — leaving, muting, or the meeting ending. */
  flush(): Segment | null {
    if (this.#segmentStartMs === null) return null
    return this.#cut()
  }

  #cut(): Segment | null {
    const startMs = this.#segmentStartMs!
    const endMs = this.#elapsedMs
    const speechMs = this.#speechMs
    const pcm = concat(this.#buffer, this.#bufferSamples)

    this.#buffer = []
    this.#bufferSamples = 0
    this.#segmentStartMs = null
    this.#silenceMs = 0
    this.#speechMs = 0

    // A cough, a door, a keyboard: not worth an inference call, and exactly
    // what makes Whisper hallucinate a polite sentence.
    if (speechMs < this.#minSpeechMs) return null
    return { startMs, endMs, pcm }
  }
}

function concat(parts: Int16Array[], total: number): Int16Array {
  const out = new Int16Array(total)
  let offset = 0
  for (const part of parts) {
    out.set(part, offset)
    offset += part.length
  }
  return out
}

/**
 * 48 kHz (what browsers give you) → 16 kHz (what Whisper wants), by
 * averaging each run of input samples. Averaging rather than picking every
 * third sample is a crude low-pass, which keeps aliasing out of the speech
 * band.
 */
export function downsampleToPcm16(
  input: Float32Array,
  inputRate: number,
  outputRate = MEETING_SAMPLE_RATE
): Int16Array {
  if (inputRate === outputRate) return floatToPcm16(input)
  const ratio = inputRate / outputRate
  const outLength = Math.floor(input.length / ratio)
  const out = new Int16Array(outLength)
  for (let i = 0; i < outLength; i++) {
    const from = Math.floor(i * ratio)
    const to = Math.min(Math.floor((i + 1) * ratio), input.length)
    let sum = 0
    for (let j = from; j < to; j++) sum += input[j]!
    out[i] = clampToPcm16(sum / Math.max(1, to - from))
  }
  return out
}

function floatToPcm16(input: Float32Array): Int16Array {
  const out = new Int16Array(input.length)
  for (let i = 0; i < input.length; i++) out[i] = clampToPcm16(input[i]!)
  return out
}

function clampToPcm16(sample: number): number {
  const clamped = Math.max(-1, Math.min(1, sample))
  return Math.round(clamped < 0 ? clamped * 0x8000 : clamped * 0x7fff)
}
